'use client'

import { type RefObject, useEffect } from 'react'

/**
 * How far past the scroll boundary the next page starts loading, so a fast
 * scroll meets rendered cards instead of the end of the list.
 */
const SENTINEL_ROOT_MARGIN = '400px'

/**
 * Walk up from `el` to the nearest scrollable ancestor.
 *
 * The galleries and the events table scroll in a nested `overflow-y-auto`
 * pane, NOT the viewport, so an observer must root THERE: with `root: null`
 * the pane's clip still hides the sentinel while the expanded viewport rect
 * does not follow it into the pane, and the prefetch margin silently buys
 * nothing. `null` (the viewport) is the honest answer while nothing above
 * `el` scrolls yet — a pane whose content still fits.
 */
export function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    if (
      (overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

/**
 * Call `onReachEnd` while `enabled`, whenever the bottom-of-list sentinel
 * comes within `SENTINEL_ROOT_MARGIN` of the pane's scroll boundary.
 *
 * The observer is rebuilt whenever `enabled` or `onReachEnd` changes, and a
 * fresh observer reports an already-visible sentinel immediately — that
 * re-arm is what keeps a list that is still shorter than its pane loading
 * until the pane is full. So a caller whose sentinel can survive a load
 * still in view MUST move one of the two per load: a paginated `status`
 * flipping `CanLoadMore → LoadingMore`, or a callback closing over the
 * cursor. Hold both stable and the sentinel simply stays intersecting,
 * which raises no new event, and the list stops growing.
 */
export function useInfiniteScrollSentinel(
  sentinelRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  onReachEnd: () => void
): void {
  useEffect(() => {
    const el = sentinelRef.current
    if (!(el && enabled) || typeof IntersectionObserver === 'undefined') {
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onReachEnd()
        }
      },
      { root: findScrollableAncestor(el), rootMargin: SENTINEL_ROOT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [sentinelRef, enabled, onReachEnd])
}
